import { Skeleton } from "@somethings-ui/web";
import { useMemo, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  getActivityUnavailableTooltip,
} from "@/components/conversations/activities/use-available-activity-items";
import type { AvailableActivity } from "@/lib/api/activities";
import { useToolCompletionCounts } from "@/lib/api/mentorships";
import type { UseActionAnalyticsResult } from "../use-action-analytics";
import { useActionLaunch } from "../use-action-launch";
import { ACTIVITY_TOOL_ORDER, CONTENT_TOOLS, type ContentToolConfig } from "./tool-config";
import { ActivityToolThumbnail, ContentToolThumbnail } from "./tool-thumbnail";
import { ToolRow } from "./tool-row";

interface ToolsTabProps {
  mentorshipId: string | null;
  analytics: UseActionAnalyticsResult;
}

export function ToolsTab({ mentorshipId, analytics }: ToolsTabProps) {
  const { data: completionCounts, isSuccess: countsReady } =
    useToolCompletionCounts(mentorshipId);

  const {
    activities,
    activitiesLoading,
    activitiesError,
    refetchActivities,
    selectActivity,
    canShareContent,
    contentGates,
    openContentCreate,
  } = useActionLaunch();

  const activityRows = useMemo(() => {
    const byType = new Map(activities.map((item) => [item.type, item]));
    return ACTIVITY_TOOL_ORDER.map((id) => byType.get(id)).filter(
      (item): item is AvailableActivity => Boolean(item),
    );
  }, [activities]);

  const handleActivityClick = (activity: AvailableActivity) => {
    analytics.trackToolSelect("activity", activity.type);
    selectActivity(activity);
  };

  const handleContentClick = (tool: ContentToolConfig) => {
    openContentCreate(tool.id);
  };

  const visibleContent = CONTENT_TOOLS.filter((tool) => contentGates[tool.id]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col pb-4">
        <ToolGroup label="ACTIVITY">
          {activitiesLoading ? (
            <ToolRowsSkeleton />
          ) : activitiesError ? (
            <ToolsError onRetry={refetchActivities} />
          ) : activityRows.length > 0 ? (
            activityRows.map((activity) => {
              const tooltip = getActivityUnavailableTooltip(activity);
              const disabled = !activity.enabled || !activity.isTeenEligible;
              return (
                <ToolRow
                  key={activity.type}
                  thumbnail={<ActivityToolThumbnail id={activity.type} />}
                  title={activity.label}
                  subtitle={
                    countsReady
                      ? `Completed ${
                          completionCounts.counts[activity.type] ?? 0
                        } times`
                      : undefined
                  }
                  disabled={disabled}
                  tooltip={tooltip || undefined}
                  onClick={() => handleActivityClick(activity)}
                />
              );
            })
          ) : (
            <p className="px-2 py-2 font-body text-body-3 text-text-tertiary">
              No activities right now - check back soon.
            </p>
          )}
        </ToolGroup>

        {canShareContent && visibleContent.length > 0 ? (
          <>
            <ToolGroupDivider />
            <ToolGroup label="CONTENT">
              {visibleContent.map((tool) => (
                <ToolRow
                  key={tool.id}
                  thumbnail={<ContentToolThumbnail id={tool.id} />}
                  title={tool.label}
                  onClick={() => handleContentClick(tool)}
                />
              ))}
            </ToolGroup>
          </>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

function ToolGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="flex flex-col">
      <div className="px-4 pb-2 pt-4">
        <span className="font-body text-[10px] uppercase tracking-wide text-text-tertiary">
          {label}
        </span>
      </div>
      <div className="flex flex-col px-2">{children}</div>
    </section>
  );
}

function ToolsError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-1 px-2 py-2">
      <p className="font-body text-body-3 text-text-tertiary">
        Couldn&apos;t load activities.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="font-body text-body-3 font-medium text-text-primary underline underline-offset-2 hover:opacity-80"
      >
        Try again
      </button>
    </div>
  );
}

function ToolRowsSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex w-full items-center gap-3 p-2">
          <Skeleton className="size-9 shrink-0 rounded-[10px]" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </>
  );
}

function ToolGroupDivider() {
  return (
    <div className="px-4 py-2">
      <div className="h-px w-full bg-surfaces-divider" />
    </div>
  );
}
